import { Fragment, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { associateContactsToHousehold } from 'api/households';
import { ContactAssociation, createRelationships } from 'api/relationships';
import { UseFormRelationshipPicker } from 'assets/js/pages/contacts/UseFormRelationshipPicker';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { ListItem } from 'components/list-item';
import { SelectItemWithLabel } from 'components/select';
import * as Select from 'components/select';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useCurrentContactRelationships } from 'customHooks/useCurrentContactRelationships';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteHouseholds } from 'customHooks/useInfiniteHouseholds';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Household } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface HouseholdJoinFormValues {
    selectedHousehold: SelectItemWithLabel<Household>[];
    relationships: {
        relationshipToContact: string;
        relationshipFromContact: string;
    }[];
}

const ContactHouseholdJoinFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const { data: currentRelationships = [] } = useCurrentContactRelationships();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 1000);

    const { ref, data } = useInfiniteHouseholds(
        {
            query: debouncedSearch,
        },
        {
            staleTime: Infinity,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        },
    );
    const households = data?.pages ?? [];

    const methods = useGivebutterForm<HouseholdJoinFormValues>({
        defaultValues: {
            selectedHousehold: [],
            relationships: [],
        },
    });
    const { control, handleSubmit, resetFormButton, path, setValue, watch, register } = methods;

    const rawSelectedHousehold = watch('selectedHousehold');
    const selectedHousehold = rawSelectedHousehold?.[0];
    const rawContacts = selectedHousehold?.value?.contacts ?? [];
    const relatedContacts = rawContacts.filter(
        contact =>
            !currentRelationships.find(
                currentRelationship => currentRelationship.related_contact.id === contact.id,
            ),
    );

    const { mutate: createRelationshipsForNewHouseholdMember } = useMutation({
        mutationFn: (formValues: HouseholdJoinFormValues) => {
            const { relationships } = formValues;

            const nextRelationships = relationships
                .reduce((prev, curr, index) => {
                    const contact = relatedContacts[index];
                    const relationshipTuple = [
                        {
                            type: curr.relationshipToContact,
                            relatedContactId: contact.id,
                            contactId: contactId,
                        },
                        {
                            type: curr.relationshipFromContact,
                            relatedContactId: contactId,
                            contactId: contact.id,
                        },
                    ];

                    return [...prev, ...relationshipTuple];
                }, [] as ContactAssociation[])
                .filter(relationship => !!relationship.type);

            if (nextRelationships.length === 0) {
                return Promise.resolve(null);
            }

            return createRelationships(accountId, nextRelationships);
        },
        onSettled: () => {
            toast.success('Joined household successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
            goBack();
        },
    });

    const { mutate: submit } = useMutation({
        mutationFn: (_: HouseholdJoinFormValues) => {
            return associateContactsToHousehold(accountId, selectedHousehold.value?.id as number, [
                contactId,
            ]);
        },
        onSuccess: async (_, formValues) => {
            createRelationshipsForNewHouseholdMember(formValues);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message ?? `Failed to join household. Please try again.`,
            );
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<HouseholdJoinFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Join Household</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        <input
                            type="hidden"
                            {...register('selectedHousehold', {
                                required: 'Please select a household',
                            })}
                        />
                        <Form.Container>
                            <Label htmlFor={'contact-search'} className={'text-sm font-medium'}>
                                Search for existing household
                            </Label>
                            {selectedHousehold ? (
                                <div className={'flex items-center gap-2'}>
                                    <p>{selectedHousehold.value?.name}</p>
                                    <Button
                                        variant="none"
                                        color="primary"
                                        className="font-medium"
                                        onClick={() => {
                                            setValue('selectedHousehold', []);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            ) : (
                                <Select.UseFormSearchableSelect
                                    control={control}
                                    name={'selectedHousehold'}
                                    showTags={true}
                                    multi={false}
                                    clearable={true}
                                    localSearch={false}
                                    inputProps={{
                                        placeholder: 'Search households',
                                        onChange: e => {
                                            setSearch(e.currentTarget.value);
                                        },
                                    }}
                                    onSelectItem={value => {
                                        if (!value) {
                                            setSearch('');
                                        }
                                    }}
                                >
                                    {households.length === 0 && (
                                        <ListItem>No households found.</ListItem>
                                    )}
                                    {households.map(household => {
                                        return (
                                            <Select.ListItem
                                                key={household.id}
                                                value={{
                                                    id: household.id.toString(),
                                                    label: household.name,
                                                    value: household,
                                                }}
                                            >
                                                {household.name}
                                            </Select.ListItem>
                                        );
                                    })}
                                    <div ref={ref} />
                                </Select.UseFormSearchableSelect>
                            )}
                            <Form.Error name={path('selectedHousehold')} />
                        </Form.Container>
                        {!!selectedHousehold && relatedContacts.length > 0 && (
                            <>
                                <Separator className={'!-mx-8 !w-auto'} />
                                <div className={'flex flex-col gap-4'}>
                                    <div className={'flex items-center justify-between'}>
                                        <div className={'flex items-center gap-1'}>
                                            <p className={'text-base font-medium text-gray-800'}>
                                                Attach new relationships
                                            </p>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger
                                                    asChild={true}
                                                    tabIndex={0}
                                                    className={`cursor-help text-gray-600`}
                                                >
                                                    <FontAwesomeIcon
                                                        size={'sm'}
                                                        icon={faQuestionCircle}
                                                    />
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content
                                                        className={
                                                            'w-[200px] !bg-gray-900 !px-3 !py-2 text-xs font-medium !text-white'
                                                        }
                                                        inverse={true}
                                                    >
                                                        Enhancing your contacts with relationship
                                                        details provides a clearer understanding of
                                                        your supporters.
                                                        <Tooltip.Arrow inverse={true} />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </div>
                                        <p className={'text-sm italic text-gray-600'}>Optional</p>
                                    </div>
                                    <Box className={'gap bg-gray-25 flex flex-col gap-4 p-4'}>
                                        {relatedContacts.map((relatedContact, index) => (
                                            <Fragment key={relatedContact.id}>
                                                {index > 0 && (
                                                    <Separator className={'!-mx-4 !w-auto'} />
                                                )}
                                                <UseFormRelationshipPicker
                                                    contact={relatedContact}
                                                    relationToPath={path(
                                                        `relationships.${index}.relationshipToContact`,
                                                    )}
                                                    relationFromPath={path(
                                                        `relationships.${index}.relationshipFromContact`,
                                                    )}
                                                />
                                            </Fragment>
                                        ))}
                                    </Box>
                                </div>
                            </>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton>Add to household</Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { ContactHouseholdJoinFormDrawer };
